import { createContext, useContext, useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";

const WebSocketContext = createContext();

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [lastMessage, setLastMessage] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const websocketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectIntervalRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000; // 3 seconds
  const reconnectIntervalDelay = 5000; // 5 seconds for interval-based reconnect

  const getWebSocketUrl = () => {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const host = window.location.host;
    return `${protocol}//${host}/ws/webclient`;
  };

  const getTokensFromCookies = () => {
    const jwtToken = Cookies.get("sensolus-token");
    const csrfToken = Cookies.get("_csrf");

    return { jwtToken, csrfToken };
  };

  // Start interval-based reconnection attempts
  const startReconnectInterval = () => {
    // Clear any existing interval
    if (reconnectIntervalRef.current) {
      clearInterval(reconnectIntervalRef.current);
    }

    setConnectionStatus("reconnecting");

    reconnectIntervalRef.current = setInterval(() => {
      // Check if already connected
      if (websocketRef.current?.readyState === WebSocket.OPEN) {
        clearInterval(reconnectIntervalRef.current);
        reconnectIntervalRef.current = null;
        return;
      }

      // Try to reconnect
      const { jwtToken, csrfToken } = getTokensFromCookies();
      if (jwtToken && csrfToken) {
        connect();
      }
    }, reconnectIntervalDelay);
  };

  // Stop interval-based reconnection
  const stopReconnectInterval = () => {
    if (reconnectIntervalRef.current) {
      clearInterval(reconnectIntervalRef.current);
      reconnectIntervalRef.current = null;
    }
  };

  const connect = (jwtToken, csrfToken) => {
    if (websocketRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    // Use provided tokens or fall back to cookies
    let actualJwtToken = jwtToken;
    let actualCsrfToken = csrfToken;

    if (!actualJwtToken || !actualCsrfToken) {
      const cookieTokens = getTokensFromCookies();
      actualJwtToken = actualJwtToken || cookieTokens.jwtToken;
      actualCsrfToken = actualCsrfToken || cookieTokens.csrfToken;
    }

    if (!actualJwtToken || !actualCsrfToken) {
      setConnectionStatus("missing_tokens");
      return;
    }

    try {
      const wsUrl = `${getWebSocketUrl()}?token=${encodeURIComponent(
        actualJwtToken
      )}&csrf=${encodeURIComponent(actualCsrfToken)}`;

      setConnectionStatus("connecting");

      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setIsConnected(true);
        setConnectionStatus("connected");
        reconnectAttempts.current = 0;

        // Clear any pending reconnect attempts
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }

        // Stop interval-based reconnection if active
        stopReconnectInterval();
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          // Handle session info message specially
          if (message.type === "session_info") {
            setSessionId(message.sessionId);
          }

          setLastMessage(message);
        } catch (error) {
          console.error("Failed to parse WebSocket message:", error);
          setLastMessage({ type: "raw", data: event.data });
        }
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        setConnectionStatus("disconnected");
        websocketRef.current = null;

        // Attempt reconnection if it wasn't a clean close (1000 = intentional close)
        if (event.code !== 1000) {
          const {
            jwtToken: currentJwtToken,
            csrfToken: currentCsrfToken,
          } = getTokensFromCookies();

          if (currentJwtToken && currentCsrfToken) {
            // Try immediate reconnect first (up to maxReconnectAttempts)
            if (reconnectAttempts.current < maxReconnectAttempts) {
              reconnectAttempts.current++;
              setConnectionStatus("reconnecting");

              reconnectTimeoutRef.current = setTimeout(() => {
                connect();
              }, reconnectDelay);
            } else {
              // After max attempts, switch to interval-based reconnection
              startReconnectInterval();
            }
          } else {
            // Tokens missing - start interval to keep checking
            startReconnectInterval();
          }
        }
      };

      ws.onerror = () => {
        setConnectionStatus("error");
      };

      websocketRef.current = ws;
    } catch (error) {
      console.error("Failed to create WebSocket connection:", error);
      setConnectionStatus("error");
    }
  };

  const disconnect = () => {
    // Clear any pending reconnect attempts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Stop interval-based reconnection
    stopReconnectInterval();

    // Reset reconnect attempts
    reconnectAttempts.current = maxReconnectAttempts; // Prevent reconnection

    if (websocketRef.current) {
      websocketRef.current.close(1000, "User logout"); // Clean close
      websocketRef.current = null;
    }

    setIsConnected(false);
    setConnectionStatus("disconnected");
    setLastMessage(null);
    setSessionId(null);
  };

  const sendMessage = (message) => {
    if (websocketRef.current?.readyState === WebSocket.OPEN) {
      const messageStr =
        typeof message === "string" ? message : JSON.stringify(message);
      websocketRef.current.send(messageStr);
      return true;
    }
    return false;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (reconnectIntervalRef.current) {
        clearInterval(reconnectIntervalRef.current);
      }
      if (websocketRef.current) {
        websocketRef.current.close();
      }
    };
  }, []);

  const value = {
    isConnected,
    connectionStatus,
    lastMessage,
    sessionId,
    connect,
    disconnect,
    sendMessage,
    reconnectAttempts: reconnectAttempts.current,
    maxReconnectAttempts,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketContext;
